import { Controller } from "@hotwired/stimulus"

// Reveal + copy the winner's CPF (PIX key) on the event payouts report (#798).
//
// Security/LGPD shape:
// - The CPF is NEVER in the initial HTML or in data-* — only the masked form is
//   server-rendered. The full number is fetched on an explicit click from a
//   POST JSON endpoint that audits each access.
// - Copy puts the raw digits (no formatting) on the clipboard for the gateway,
//   while the DOM shows the formatted number for the operator to eyeball.
// - All user-facing text comes from i18n via values (no hardcoded strings).
export default class extends Controller {
  static targets = ["display", "revealBtn", "copyBtn"]
  static values = {
    url: String,
    copiedLabel: String,
    copyLabel: String,
    revealLabel: String,
    revealErrorLabel: String,
    rateLimitedLabel: String
  }

  // Raw digits, held in memory only after a successful reveal — never written
  // to a data attribute or the page source.
  #digits = null

  async reveal(event) {
    event.preventDefault()
    if (this.#digits) return

    const headers = { "Accept": "application/json" }
    // csrf_meta_tags renders nothing when forgery protection is off (test env),
    // so the meta may be absent — only send the header when it exists.
    const csrf = document.querySelector('meta[name="csrf-token"]')
    if (csrf) headers["X-CSRF-Token"] = csrf.content

    // Disable before the await so a double-click cannot fire two fetches (and
    // two audit rows). On success the button is hidden; on error it is restored.
    this.revealBtnTarget.disabled = true

    let response
    try {
      response = await fetch(this.urlValue, { method: "POST", headers })
    } catch {
      // Network failure (offline, DNS, CORS) — still surface it, never silent.
      this.#showError()
      return
    }

    if (!response.ok) {
      // lei "always give feedback": 403/429/5xx must not leave the button inert.
      // Differentiate the rate-limit case; never expose any digits in the error.
      this.#showError(response.status === 429)
      return
    }

    const data = await response.json()
    if (!data.cpf) {
      this.#showError()
      return
    }

    this.#digits = data.cpf
    this.displayTarget.textContent = this.#format(data.cpf)
    this.revealBtnTarget.hidden = true
    if (this.hasCopyBtnTarget) this.copyBtnTarget.hidden = false
  }

  // Flash an i18n error label on the reveal button, then restore it so the
  // operator can retry. Never carries the CPF — only a static message.
  #showError(rateLimited = false) {
    const btn = this.revealBtnTarget
    btn.textContent = rateLimited ? this.rateLimitedLabelValue : this.revealErrorLabelValue
    setTimeout(() => {
      btn.textContent = this.revealLabelValue
      btn.disabled = false
    }, 3000)
  }

  async copy(event) {
    event.preventDefault()
    if (!this.#digits) return

    await this.#writeToClipboard(this.#digits)

    // Always give feedback after the copy attempt — the operator must know it
    // landed (lei: never fail silently).
    const btn = this.copyBtnTarget
    btn.textContent = this.copiedLabelValue
    setTimeout(() => { btn.textContent = this.copyLabelValue }, 2000)
  }

  // Async Clipboard API needs a secure context; under headless HTTP (and older
  // browsers) it is unavailable, so fall back to a throwaway textarea + the
  // legacy execCommand path. Never throws.
  async #writeToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)
        return
      } catch {
        // fall through to legacy
      }
    }
    const scratch = document.createElement("textarea")
    scratch.value = text
    scratch.setAttribute("readonly", "")
    scratch.style.position = "absolute"
    scratch.style.left = "-9999px"
    document.body.appendChild(scratch)
    scratch.select()
    try { document.execCommand("copy") } catch { /* best effort */ }
    document.body.removeChild(scratch)
  }

  #format(digits) {
    const d = String(digits).replace(/\D/g, "")
    if (d.length !== 11) return d
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`
  }
}
